import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import axios from "axios";
import { auth } from 'firebase/app';
import { AngularFireAuth } from '@angular/fire/auth';
import {
  AngularFirestore,
  AngularFirestoreDocument
} from '@angular/fire/firestore';

import { Observable, combineLatest, of } from 'rxjs';
import { switchMap, first, map } from 'rxjs/operators';
import { SharedService } from "./shared.service";
import { CryptoService } from "./crypto.service";
import { environment } from '../../environments/environment';
import * as moment from 'moment-timezone';
import * as Payjp from 'payjp';
const payjp = Payjp(environment.payjp);

const emails = require('./emails.json');

@Injectable({ providedIn: 'root' })
export class AuthService {
  user$: Observable<any>;
  showSpinner: boolean = false;
  user: any;
  currentAuthUserID: string = '';

  static USER_ROLE: string = 'user';
  static VTUBER_ROLE: string = 'vtuber';

  static VTUBER_REVIEW_STATUS: string = 'review';
  static VTUBER_COMPLETD_STATUS: string = 'approve';
  static VTUBER_REJECT_STATUS: string = 'reject';

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private router: Router,
    private shared: SharedService,
    private crypto: CryptoService
  ) {
    this.user$ = this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          this.currentAuthUserID = user.uid
          if (this.user === undefined) {
            this.fetchUser()
          }
          return this.afs.doc<any>(`users/${user.uid}`).valueChanges();
        } else {
          return of(null);
        }
      })
    );
  }

  getEmailTemplate(value) {
    return value.replace(/--site_url--/g, environment.site_url)
  }

  getVtubersByPlanID(planID) {
    return this.afs.collection('users', ref => ref.where('planIDs', 'array-contains', planID))
      .get()
      .pipe(
        map(actions => {
          if (actions.docs.length > 0) {
            let res = []
            actions.docs.map(item => {
              res.push(item.data())
            })

            return res
          }
          return []
        })
      );
  }

  getUserByUid(uid) {
    return this.afs.collection('users', ref => ref.where('uid', '==', uid))
      .get()
      .pipe(
        map(actions => {
          if (actions.docs.length > 0) {
            let res = []
            actions.docs.map(item => {
              res.push(item.data())
            })

            return res
          }
          return []
        })
      )
  }

  getVtubers() {
    return this.afs.collection('users', ref => ref.where('role', '==', AuthService.VTUBER_ROLE).where('status', '==', AuthService.VTUBER_COMPLETD_STATUS))
      .get()
      .pipe(
        map(actions => {
          if (actions.docs.length > 0) {
            let res = []
            actions.docs.map(item => {
              if (item.data().events && item.data().events.length > 0 && item.data().is_avatar_tested && item.data().is_public !== false) {
                res.push(item.data())
              }
            })

            return res
          }
          return []
        })
      )
  }

  getUserPayHistories() {
    return this.afs.collection('histories', ref => ref.where('user_id', '==', this.user.uid).orderBy('createdAt', 'desc'))
      .get()
      .pipe(
        map(actions => {
          if (actions.docs.length > 0) {
            let res = []
            actions.docs.map(item => {
              res.push(item.data())
            })

            return res
          }
          return []
        })
      )
  }

  getUser() {
    return this.user$.pipe(first()).toPromise();
  }

  setUser(obj) {
    this.user = obj
  }

  googleSignIn() {
    const provider = new auth.GoogleAuthProvider();
    return this.oAuthLogin(provider);
  }

  twitterSignIn() {
    const provider = new auth.TwitterAuthProvider();
    return this.oAuthLogin(provider);
  }

  googleSignUp(role) {
    const provider = new auth.GoogleAuthProvider();
    return this.oAuthSignUp(provider, role, 'google');
  }

  twitterSignUp(role) {
    const provider = new auth.TwitterAuthProvider();
    return this.oAuthSignUp(provider, role, 'twitter')
  }

  private async oAuthLogin(provider) {
    return this.afAuth.auth.signInWithPopup(provider);
  }

  private async oAuthSignUp(provider, role, source) {
    const credential = await this.afAuth.auth.signInWithPopup(provider);

    let user = await this.getUser();
    if (user) {
      await this.afAuth.auth.signOut();
      this.user = undefined
      this.shared.removeCookie(SharedService.CURRENT_VTUBER);

      throw new Error('already_registered');
    }
    if (credential.user.email == null) {
      this.afAuth.auth.currentUser.delete();
      throw new Error('no_email');
    }

    let params = {
      source: source,
      role: role,
      uid: credential.user.uid,
      displayName: credential.user.displayName,
      photoURL: credential.user.photoURL,
      email: this.encrypt(credential.user.email),
      twitter_url: '',
      youtube_url: '',
      createdAt: Date.now()
    }

    if (role === AuthService.VTUBER_ROLE && source === 'twitter') {
      params.twitter_url = "https://twitter.com/" + credential.additionalUserInfo.username
    }

    if (role === AuthService.VTUBER_ROLE && source === 'google') {
      params.youtube_url = "https://youtube.com/" + credential.additionalUserInfo.username
    }

    this.sendEmail({
      to: this.encrypt(credential.user.email),
      subject: emails.register_subject,
      text: this.getEmailTemplate(emails.register.join("")).replace(/\[ユーザー名\]/g, credential.user.displayName)
    })

    return this.updateUserData(params);
  }

  public updateTwitterUrl({uid, twitter_url}) {
    const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${uid}`);

    const data = {
      uid,
      twitter_url
    };

    return userRef.set(data, { merge: true });
  }

  public updateUserData(data, merge = true) {
    const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${data.uid}`);

    return userRef.set(data, { merge });
  }

  async signOut() {
    await this.afAuth.auth.signOut();
    this.user = undefined
    this.shared.removeCookie(SharedService.CURRENT_VTUBER);

    return this.router.navigate(['/']);
  }

  twitterLink() {
    const provider = new auth.TwitterAuthProvider();
    return this.linkup(provider)
  }

  googleLink() {
    const provider = new auth.GoogleAuthProvider();
    return this.linkup(provider)
  }

  unlink(source) {
    this.afAuth.auth.currentUser.providerData.map(item => {
      if (item.providerId === source) {
        this.afAuth.auth.currentUser.unlink(item.providerId)
      }
    });
  }

  linkup(provider) {
    return this.afAuth.auth.currentUser.linkWithPopup(provider)
  }

  signOutWithoutRedirect() {
    this.afAuth.auth.signOut();
  }

  emailSignIn({email, password}) {
    return this.afAuth.auth.signInAndRetrieveDataWithEmailAndPassword(email, password);
  }

  emailSignUp({email, password}) {
    return this.afAuth.auth.createUserAndRetrieveDataWithEmailAndPassword(email, password);
  }

  canPasswordReset(email) {
    return this.afAuth.auth.fetchSignInMethodsForEmail(email);
  }

  passwordResetEmail(email) {
    this.afAuth.auth.sendPasswordResetEmail(email);
  }

  updatePassword(password) {
    return this.afAuth.auth.currentUser.updatePassword(password);
  }

  updateEmail(email) {
    return this.afAuth.auth.currentUser.updateEmail(email);
  }

  toggleSpinner(value) {
    this.showSpinner = value
  }

  fetchUser() {
    if (this.currentAuthUserID !== '') {
      this.afs.doc<any>(`users/${this.currentAuthUserID}`).get().toPromise()
      .then(response => {
        if (response.exists) {
          this.setUser(response.data())
        }
      })
    }
  }

  fetchVTuber(uid) {
    return this.afs.doc<any>(`users/${uid}`).get().toPromise();
  }

  // Send Notification
  public sendNotification(data) {
    this.afs.collection('notifications').add(data)
  }

  formatDate(date) {
    let value = moment(date).tz('Asia/Tokyo');
    return value.format('Y-MM-DD')
  }

  futureMonth() {
    let currentDate = moment().tz('Asia/Tokyo');
    if (currentDate.date() < 10) return currentDate.format('Y-MM-DD');

    let futureMonth = moment(currentDate).add(1, 'M');
    let futureMonthEnd = moment(futureMonth).endOf('month');

    if(currentDate.date() != futureMonth.date() && futureMonth.isSame(futureMonthEnd.format('YYYY-MM-DD'))) {
      futureMonth = futureMonth.add(1, 'd');
    }

    return futureMonth.format("Y-MM-DD");
  }

  parseMonth(date) {
    let value = moment(date).tz('Asia/Tokyo')
    return value.format('Y-MM')
  }

  parseStringToEventTime(value) {
    let times = value.split('~');
    return {
      start: {
        hour: parseInt(times[0].split(':')[0]),
        minute: parseInt(times[0].split(':')[1])
      },
      end: {
        hour: parseInt(times[1].split(':')[0]),
        minute: parseInt(times[1].split(':')[1])
      }
    }
  }

  getSupporters() {
    let user;
    const joinKeys = {};

    return this.user$.pipe(
      switchMap(u => {
        // Unique User IDs
        user = u;
        let uids = [];
        user.supporters.map(supporter => {
          if (!uids.includes(supporter.user_id)) {
            uids.push(supporter.user_id)
          }
        })

        // Firestore User Doc Reads
        const userDocs = uids.map(u =>
          this.afs.doc(`users/${u}`).valueChanges()
        );

        return userDocs.length ? combineLatest(userDocs) : of([]);
      }),
      map(arr => {
        arr.forEach(v => (joinKeys[(<any>v).uid] = v));
        user.supporters = user.supporters.map(v => {
          return {
            ...v,
            plan: user.plans.filter(item => item.uid === v.plan_uid)[0],
            user: {
              uid: joinKeys[v.user_id].uid,
              photoURL: joinKeys[v.user_id].photoURL,
              displayName: joinKeys[v.user_id].displayName
            }
          }
        });

        return user.supporters;
      })
    )
  }

  getSupportedVtubers() {
    let user;
    const joinKeys = {};

    return this.user$.pipe(
      switchMap(u => {
        // Unique User IDs
        user = u;
        let uids = [];
        user.plans.map(plan => {
          if (!uids.includes(plan.vtuber_id)) {
            uids.push(plan.vtuber_id)
          }
        })

        // Firestore User Doc Reads
        const userDocs = uids.map(u =>
          this.afs.doc(`users/${u}`).valueChanges()
        );

        return userDocs.length ? combineLatest(userDocs) : of([]);
      }),
      map(arr => {
        arr.forEach(v => (joinKeys[(<any>v).uid] = v));
        user.plans = user.plans.map(v => {
          return {
            ...v,
            plan: joinKeys[v.vtuber_id].plans.filter(item => item.uid === v.plan_uid)[0],
            vtuber: {
              uid: joinKeys[v.vtuber_id].uid,
              photoURL: joinKeys[v.vtuber_id].photoURL,
              displayName: joinKeys[v.vtuber_id].displayName,
              supporters: joinKeys[v.vtuber_id].supporters
            },
            change: v.change ? {
              changedAt: v.change.changedAt,
              plan: joinKeys[v.vtuber_id].plans.filter(item => item.uid === v.change.plan_uid)[0]
            } : undefined
          }
        });

        return user.plans;
      })
    )
  }

  generateUrls(params) {
    let url = "";
    let keys = Object.keys(params)
    keys.map(key => {
      url += (params[key]) ? '&' + key + '=' + encodeURI(params[key]) : '';
    })

    return url;
  }

  sendEmail(params) {
    params.to = this.decrypt(params.to)
    axios.post(environment.function_url + "sendEmail?" + this.generateUrls(params))
  }

  sendProfileReview() {
    axios.post(environment.function_url + "sendReviewRequest?" + this.generateUrls({
      userid: this.user.uid
    }))
  }

  async createToken(number, month, year, cvc) {
    let params = {
      number: number,
      month: month,
      year: year,
      cvc: cvc
    }

    // return payjp.tokens.create({
    //   card: params
    // }, {
    //   'X-Payjp-Direct-Token-Generate': 'true'
    // })
    return axios.post(environment.function_url + "createToken?" + this.generateUrls(params))
  }

  async createCustomer(token, name) {
    let params = {
      token: token,
      name: name,
      cid: this.user.card_customer ? this.crypto.decrypt(this.user.card_customer.id) : 'new',
      email: this.decrypt(this.user.email)
    }

    return axios.post(environment.function_url + "createCustomer?" + this.generateUrls(params))
  }

  async doPay(amount) {
    let params = {
      cid: this.user.card_customer ? this.crypto.decrypt(this.user.card_customer.id) : 'new',
      amount: amount
    }

    return axios.post(environment.function_url + "doPay?" + this.generateUrls(params))
  }

  public encrypt(value) {
    return this.crypto.encrypt(value)
  }

  public decrypt(value) {
    return this.crypto.decrypt(value)
  }
}
